// 随机数据生成工具
import {
  chinaAddressData,
  supportedCountries,
  getCountryAddressData,
  ChinaDistrict,
} from './addressData';
import { lookupBIN, getCardTypeName, getCardDescription, BINInfo } from './binDatabase';
import { calculateLuhnCheckDigitOptimized } from './luhnOptimized';

// 导出 BIN 查询功能
export { lookupBIN, getCardTypeName, getCardDescription };
export type { BINInfo };

// 中国常见姓氏
const surnames = ['张', '王', '李', '赵', '刘', '陈', '杨', '黄', '周', '吴', '徐', '孙', '马', '朱', '胡', '郭', '何', '高', '林', '罗', '梁', '宋', '郑', '谢', '韩', '唐', '冯', '于', '董', '萧', '程', '曹', '袁', '邓', '许', '傅', '沈', '曾', '彭', '吕', '苏', '卢', '蒋', '蔡', '贾', '丁', '魏', '薛', '叶', '阎'];

// 中国常见名字
const givenNames = ['伟', '芳', '娜', '敏', '静', '丽', '强', '磊', '军', '洋', '勇', '艳', '杰', '娟', '涛', '明', '超', '秀', '霞', '平', '燕', '辉', '刚', '华', '建', '文', '斌', '英', '玲', '红', '波', '鹏', '飞', '云', '龙', '凤', '婷', '雪', '琳', '欣', '怡', '佳', '俊', '宇', '浩', '晨', '阳', '晓', '雨', '思'];

// 英文名字
const englishFirstNames = ['James', 'John', 'Robert', 'Michael', 'William', 'David', 'Richard', 'Joseph', 'Thomas', 'Charles', 'Mary', 'Patricia', 'Jennifer', 'Linda', 'Elizabeth', 'Barbara', 'Susan', 'Jessica', 'Sarah', 'Karen', 'Emma', 'Olivia', 'Ava', 'Isabella', 'Sophia', 'Mia', 'Charlotte', 'Amelia', 'Harper', 'Evelyn'];
const englishLastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White', 'Harris'];

// 日文名字
const japaneseFirstNames = ['太郎', '次郎', '健太', '翔太', '大輝', '拓海', '蓮', '悠真', '陽翔', '湊', '花子', '美咲', '結衣', '陽菜', '葵', '凛', '楓', '莉子', '芽依', '咲良'];
const japaneseLastNames = ['佐藤', '鈴木', '高橋', '田中', '伊藤', '渡辺', '山本', '中村', '小林', '加藤', '吉田', '山田', '佐々木', '山口', '松本', '井上', '木村', '林', '斎藤', '清水'];

// 韩文名字
const koreanFirstNames = ['민준', '서준', '도윤', '예준', '시우', '하준', '주원', '지호', '지후', '준서', '서연', '서윤', '지우', '서현', '민서', '하은', '하윤', '윤서', '지민', '채원'];
const koreanLastNames = ['김', '이', '박', '최', '정', '강', '조', '윤', '장', '임', '한', '오', '서', '신', '권', '황', '안', '송', '류', '전'];

// 中国街道名称
const chinaStreets = ['建国路', '人民路', '中山路', '解放路', '和平路', '友谊路', '光明路', '幸福路', '胜利路', '东风路', '西湖路', '南京路', '北京路', '上海路', '广州路', '长安街', '天河路', '滨江路', '文化路', '科技路', '创业路', '金融街', '商业街', '步行街', '学院路', '大学路', '体育路', '公园路', '湖滨路', '江滨路'];

export interface AccountInfo {
  holderName: string;
  country: string;
  countryCode: string;
  province: string;
  city: string;
  district: string;
  postalCode: string;
  street: string;
  addressLine2: string;
}

// 获取所有支持的国家
export const getSupportedCountries = () => supportedCountries;

// 获取中国省份列表
export const getChinaProvinces = (): string[] => {
  return chinaAddressData.map(p => p.name);
};

// 获取中国省份下的城市列表
export const getChinaCities = (provinceName: string): string[] => {
  const province = chinaAddressData.find(p => p.name === provinceName);
  return province ? province.cities.map(c => c.name) : [];
};

// 获取中国城市下的区县列表
export const getChinaDistricts = (provinceName: string, cityName: string): ChinaDistrict[] => {
  const province = chinaAddressData.find(p => p.name === provinceName);
  if (!province) return [];
  const city = province.cities.find(c => c.name === cityName);
  return city ? city.districts : [];
};

// 获取海外国家的州/省列表
export const getOverseasStates = (countryCode: string): string[] => {
  const countryData = getCountryAddressData(countryCode);
  return countryData ? countryData.states.map(s => s.name) : [];
};

// 获取海外州/省下的城市列表
export const getOverseasCities = (countryCode: string, stateName: string): string[] => {
  const countryData = getCountryAddressData(countryCode);
  if (!countryData) return [];
  const state = countryData.states.find(s => s.name === stateName);
  return state ? state.cities.map(c => c.name) : [];
};

// 获取海外城市下的区域列表
export const getOverseasDistricts = (countryCode: string, stateName: string, cityName: string) => {
  const countryData = getCountryAddressData(countryCode);
  if (!countryData) return [];
  const state = countryData.states.find(s => s.name === stateName);
  if (!state) return [];
  const city = state.cities.find(c => c.name === cityName);
  return city ? city.districts : [];
};

// 随机选择数组中的元素
const randomChoice = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

// 生成随机数字
const randomNumber = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;

// 生成随机中文姓名
export const generateRandomName = (): string => {
  const surname = randomChoice(surnames);
  const givenName1 = randomChoice(givenNames);
  const givenName2 = Math.random() > 0.5 ? randomChoice(givenNames) : '';
  return surname + givenName1 + givenName2;
};

// 生成随机英文姓名
export const generateRandomEnglishName = (): string => {
  const firstName = randomChoice(englishFirstNames);
  const lastName = randomChoice(englishLastNames);
  return `${firstName} ${lastName}`;
};

// 生成随机日文姓名
export const generateRandomJapaneseName = (): string => {
  const lastName = randomChoice(japaneseLastNames);
  const firstName = randomChoice(japaneseFirstNames);
  return `${lastName} ${firstName}`;
};

// 生成随机韩文姓名
export const generateRandomKoreanName = (): string => {
  const lastName = randomChoice(koreanLastNames);
  const firstName = randomChoice(koreanFirstNames);
  return `${lastName}${firstName}`;
};

// 根据国家生成随机姓名
export const generateNameByCountry = (countryCode: string): string => {
  switch (countryCode) {
    case 'CN': return generateRandomName();
    case 'JP': return generateRandomJapaneseName();
    case 'KR': return generateRandomKoreanName();
    default: return generateRandomEnglishName();
  }
};

// 生成门牌号
export const generateStreetNumber = (): string => {
  return randomNumber(1, 999).toString() + '号';
};

// 生成中国详细地址
export const generateChinaAddressLine2 = (): string => {
  const building = randomNumber(1, 50);
  const floor = randomNumber(1, 30);
  const room = randomNumber(1, 20);
  return `${building}栋${floor}层${room}室`;
};

// 生成海外详细地址
export const generateOverseasAddressLine2 = (countryCode: string): string => {
  const countryData = getCountryAddressData(countryCode);
  if (!countryData) return '';
  
  const format = randomChoice(countryData.addressLine2Formats);
  return format
    .replace('{apt}', randomNumber(1, 999).toString())
    .replace('{floor}', randomNumber(1, 50).toString())
    .replace('{building}', String.fromCharCode(65 + randomNumber(0, 25)))
    .replace('{room}', randomNumber(101, 9999).toString())
    .replace('{dong}', randomNumber(100, 120).toString())
    .replace('{ho}', randomNumber(101, 2505).toString());
};

// 生成海外街道地址
export const generateOverseasStreet = (countryCode: string): string => {
  const countryData = getCountryAddressData(countryCode);
  if (!countryData) return '';
  
  const format = randomChoice(countryData.streetFormats);
  return format
    .replace('{number}', randomNumber(1, 9999).toString())
    .replace('{chome}', randomNumber(1, 9).toString())
    .replace('{ban}', randomNumber(1, 30).toString())
    .replace('{go}', randomNumber(1, 20).toString())
    .replace('{dong}', ['강남', '역삼', '서초', '삼성', '청담'][randomNumber(0, 4)])
    .replace('{ro}', ['테헤란', '강남대', '논현', '도산대', '압구정'][randomNumber(0, 4)])
    .replace('{daero}', ['강남', '서초', '테헤란', '올림픽'][randomNumber(0, 3)]);
};

// 生成随机中国地址
export const generateRandomChinaAddress = (): AccountInfo => {
  // 随机选择省份
  const province = randomChoice(chinaAddressData);
  // 随机选择城市
  const city = randomChoice(province.cities);
  // 随机选择区县
  const district = randomChoice(city.districts);
  // 随机选择街道
  const street = randomChoice(chinaStreets);
  
  return {
    holderName: generateRandomName(),
    country: '中国',
    countryCode: 'CN',
    province: province.name,
    city: city.name,
    district: district.name,
    postalCode: district.postalCode,
    street: street + generateStreetNumber(),
    addressLine2: generateChinaAddressLine2(),
  };
};

// 生成随机海外地址
export const generateRandomOverseasAddress = (countryCode: string): AccountInfo | null => {
  const countryData = getCountryAddressData(countryCode);
  if (!countryData) return null;
  
  const country = supportedCountries.find(c => c.code === countryCode);
  if (!country) return null;
  
  // 随机选择州/省
  const state = randomChoice(countryData.states);
  // 随机选择城市
  const city = randomChoice(state.cities);
  // 随机选择区域
  const district = randomChoice(city.districts);
  
  return {
    holderName: generateNameByCountry(countryCode),
    country: country.name,
    countryCode: countryCode,
    province: state.name,
    city: city.name,
    district: district.name,
    postalCode: district.postalCode,
    street: generateOverseasStreet(countryCode),
    addressLine2: generateOverseasAddressLine2(countryCode),
  };
};

// 生成完整随机账单信息（支持多国）
export const generateRandomAccountInfo = (countryCode: string = 'CN'): AccountInfo => {
  if (countryCode === 'CN') {
    return generateRandomChinaAddress();
  }
  
  const overseasAddress = generateRandomOverseasAddress(countryCode);
  if (overseasAddress) {
    return overseasAddress;
  }
  
  // 默认返回中国地址
  return generateRandomChinaAddress();
};

// 使用加密安全的随机数生成器
const getSecureRandom = (): number => {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0] / (0xffffffff + 1);
  }
  return Math.random();
};

// 安全随机整数
const secureRandomInt = (min: number, max: number): number => {
  return Math.floor(getSecureRandom() * (max - min + 1)) + min;
};

// Luhn 算法查表优化 - 预计算双倍值（用于本地验证）
const LUHN_DOUBLE_TABLE = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];

// 生成随机卡号（使用加密安全随机数和优化的 Luhn 算法）
export const generateCardNumber = (template: string): string => {
  const cleaned = template.replace(/\s/g, '').toLowerCase();
  
  // 生成随机数字替换 x（除了最后一位）
  let cardNumber = '';
  for (let i = 0; i < cleaned.length - 1; i++) {
    if (cleaned[i] === 'x') {
      cardNumber += secureRandomInt(0, 9).toString();
    } else {
      cardNumber += cleaned[i];
    }
  }
  
  // 计算并添加 Luhn 校验位
  const checkDigit = calculateLuhnCheckDigitOptimized(cardNumber);
  return cardNumber + checkDigit;
};

// 根据卡类型生成正确长度的 CVV
const getCVVLength = (cardNumber: string): number => {
  // American Express 使用 4 位 CVV
  if (cardNumber.startsWith('34') || cardNumber.startsWith('37')) {
    return 4;
  }
  return 3;
};

// 生成智能 CVV（根据卡类型）
export const generateCVV = (cardNumber?: string): string => {
  const length = cardNumber ? getCVVLength(cardNumber) : 3;
  let cvv = '';
  
  for (let i = 0; i < length; i++) {
    cvv += secureRandomInt(0, 9).toString();
  }
  
  // 避免生成全 0 或全相同数字（更真实）
  if (/^0+$/.test(cvv) || /^(\d)\1+$/.test(cvv)) {
    return generateCVV(cardNumber);
  }
  
  return cvv;
};

// 生成智能有效期（避免即将过期的日期）
export const generateExpiryDate = (): { month: string; year: string } => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  
  // 生成 1-5 年后的有效期
  const yearsAhead = secureRandomInt(1, 5);
  const year = currentYear + yearsAhead;
  
  // 随机月份，但如果是明年，避免生成已过期的月份
  let month: number;
  if (yearsAhead === 1 && currentMonth > 6) {
    month = secureRandomInt(currentMonth, 12);
  } else {
    month = secureRandomInt(1, 12);
  }
  
  return {
    month: month.toString().padStart(2, '0'),
    year: year.toString().slice(-2),
  };
};

// 生成完整虚拟卡信息
export const generateCompleteVirtualCard = (template: string): {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  formattedExpiry: string;
} => {
  const cardNumber = generateCardNumber(template);
  const expiry = generateExpiryDate();
  const cvv = generateCVV(cardNumber);
  
  return {
    cardNumber,
    expiryMonth: expiry.month,
    expiryYear: expiry.year,
    cvv,
    formattedExpiry: `${expiry.month}/${expiry.year}`
  };
};

// 批量生成虚拟卡
export const generateBatchCards = (template: string, count: number): Array<{
  cardNumber: string;
  expiry: string;
  cvv: string;
  cardType: string;
}> => {
  const cards: Array<{ cardNumber: string; expiry: string; cvv: string; cardType: string }> = [];
  const seen = new Set<string>();
  
  let attempts = 0;
  const maxAttempts = count * 10;
  
  while (cards.length < count && attempts < maxAttempts) {
    attempts++;
    const cardNumber = generateCardNumber(template);
    
    // 确保不重复
    if (seen.has(cardNumber)) continue;
    seen.add(cardNumber);
    
    const expiry = generateExpiryDate();
    const cvv = generateCVV(cardNumber);
    
    cards.push({
      cardNumber,
      expiry: `${expiry.month}/${expiry.year}`,
      cvv,
      cardType: identifyCardType(cardNumber),
    });
  }
  
  return cards;
};

// 优化的 Luhn 验证（查表法）
export const validateCardNumber = (cardNumber: string): boolean => {
  const cleanNumber = cardNumber.replace(/\D/g, '');
  if (cleanNumber.length < 13 || cleanNumber.length > 19) return false;
  
  let sum = 0;
  let isEven = false;

  for (let i = cleanNumber.length - 1; i >= 0; i--) {
    const digit = parseInt(cleanNumber[i], 10);
    sum += isEven ? LUHN_DOUBLE_TABLE[digit] : digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

// 解析卡片信息字符串
export const parseCardInfo = (cardString: string): {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
} | null => {
  const parts = cardString.trim().split('|');
  if (parts.length !== 4) return null;
  
  const [cardNumber, month, year, cvv] = parts.map(part => part.trim());
  
  // 基本验证
  if (!cardNumber || !month || !year || !cvv) return null;
  if (!/^\d{13,19}$/.test(cardNumber)) return null;
  if (!/^\d{2}$/.test(month) || parseInt(month) < 1 || parseInt(month) > 12) return null;
  if (!/^\d{2,4}$/.test(year)) return null;
  if (!/^\d{3,4}$/.test(cvv)) return null;
  
  return {
    cardNumber,
    expiryMonth: month,
    expiryYear: year.length === 4 ? year.slice(-2) : year,
    cvv,
  };
};

// 格式化卡号显示（每4位加空格）
export const formatCardNumber = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\s/g, '');
  return cleaned.replace(/(.{4})/g, '$1 ').trim();
};

// 验证邮编格式（支持多国）
export const validatePostalCode = (postalCode: string, countryCode: string = 'CN'): boolean => {
  switch (countryCode) {
    case 'CN':
      return /^\d{6}$/.test(postalCode);
    case 'US':
      return /^\d{5}(-\d{4})?$/.test(postalCode);
    case 'UK':
      return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(postalCode);
    case 'JP':
      return /^\d{3}-?\d{4}$/.test(postalCode);
    case 'CA':
      return /^[A-Z]\d[A-Z]\s*\d[A-Z]\d$/i.test(postalCode);
    case 'AU':
      return /^\d{4}$/.test(postalCode);
    case 'DE':
    case 'FR':
      return /^\d{5}$/.test(postalCode);
    case 'KR':
      return /^\d{5}$/.test(postalCode);
    default:
      return postalCode.length > 0;
  }
};

// 获取邮编格式提示
export const getPostalCodeHint = (countryCode: string): string => {
  switch (countryCode) {
    case 'CN': return '6位数字邮编';
    case 'US': return '5位数字 (如 90210)';
    case 'UK': return '英国邮编 (如 SW1A 1AA)';
    case 'JP': return '日本邮编 (如 100-0001)';
    case 'CA': return '加拿大邮编 (如 M5V 3L9)';
    case 'AU': return '4位数字 (如 2000)';
    case 'DE': return '5位数字 (如 10115)';
    case 'FR': return '5位数字 (如 75001)';
    case 'KR': return '5位数字 (如 06235)';
    default: return '邮政编码';
  }
};

// 验证卡号段格式
export const validateCardNumberTemplate = (template: string): {
  isValid: boolean;
  error?: string;
} => {
  if (!template.trim()) {
    return { isValid: false, error: '卡号段不能为空' };
  }

  // 移除所有空格
  const cleaned = template.replace(/\s/g, '');
  
  // 检查长度（通常16-19位）
  if (cleaned.length < 13 || cleaned.length > 19) {
    return { isValid: false, error: '卡号段长度必须在13-19位之间' };
  }

  // 检查字符 - 只能包含数字和x
  if (!/^[0-9x]+$/i.test(cleaned)) {
    return { isValid: false, error: '卡号段只能包含数字和字母x' };
  }

  // 检查x的数量 - 至少要有一些x用于随机生成
  const xCount = (cleaned.match(/x/gi) || []).length;
  if (xCount === 0) {
    return { isValid: false, error: '卡号段必须包含至少一个x用于随机生成' };
  }

  if (xCount > 10) {
    return { isValid: false, error: 'x的数量不能超过10个' };
  }

  // 验证开头数字（银行卡BIN号段）
  const firstDigit = cleaned[0];
  const firstTwoDigits = cleaned.substring(0, 2);
  const firstThreeDigits = cleaned.substring(0, 3);
  const firstFourDigits = cleaned.substring(0, 4);
  
  // 检查是否符合主流银行卡BIN号段
  const isValidBIN = 
    // Visa: 4xxx
    firstDigit === '4' ||
    // MasterCard: 5xxx, 2221-2720
    firstDigit === '5' ||
    (firstFourDigits >= '2221' && firstFourDigits <= '2720') ||
    // American Express: 34xx, 37xx
    firstTwoDigits === '34' || firstTwoDigits === '37' ||
    // Discover: 6011, 622126-622925, 644-649, 65xx
    firstFourDigits === '6011' ||
    (firstTwoDigits >= '64' && firstTwoDigits <= '65') ||
    // JCB: 3528-3589
    (firstFourDigits >= '3528' && firstFourDigits <= '3589') ||
    // UnionPay: 62xx
    firstTwoDigits === '62' ||
    // Diners Club: 300-305, 36xx, 38xx
    (firstThreeDigits >= '300' && firstThreeDigits <= '305') ||
    firstTwoDigits === '36' || firstTwoDigits === '38' ||
    // Maestro: 5018, 5020, 5038, 5893, 6304, 6759, 6761-6763
    firstFourDigits === '5018' || firstFourDigits === '5020' ||
    firstFourDigits === '5038' || firstFourDigits === '5893' ||
    firstFourDigits === '6304' || firstFourDigits === '6759' ||
    (firstFourDigits >= '6761' && firstFourDigits <= '6763');
  
  // 借记卡 BIN 号段: 621xxx - 628xxx
  const isDebitBIN = firstThreeDigits >= '621' && firstThreeDigits <= '628';

  if (!isValidBIN && !isDebitBIN) {
    return { isValid: false, error: '无效的银行卡号段，请使用正确的BIN号段开头' };
  }

  return { isValid: true };
};

// 格式化卡号段显示
export const formatCardTemplate = (template: string): string => {
  const cleaned = template.replace(/\s/g, '');
  return cleaned.replace(/(.{4})/g, '$1 ').trim();
};

// 清理卡号段输入（只保留数字和x）
export const cleanCardTemplate = (input: string): string => {
  return input.replace(/[^0-9x]/gi, '').toUpperCase();
};

// 识别银行卡类型
export const identifyCardType = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\s/g, '');
  const firstDigit = cleaned[0];
  const firstTwoDigits = cleaned.substring(0, 2);
  const firstThreeDigits = cleaned.substring(0, 3);
  const firstFourDigits = cleaned.substring(0, 4);
  const firstSixDigits = cleaned.substring(0, 6);
  
  // Visa: 4xxx
  if (firstDigit === '4') {
    return 'Visa';
  }
  
  // MasterCard: 5xxx, 2221-2720
  if (firstDigit === '5' || (firstFourDigits >= '2221' && firstFourDigits <= '2720')) {
    return 'MasterCard';
  }
  
  // American Express: 34xx, 37xx
  if (firstTwoDigits === '34' || firstTwoDigits === '37') {
    return 'American Express';
  }
  
  // Discover: 6011, 622126-622925, 644-649, 65xx
  if (firstFourDigits === '6011' || 
      (firstTwoDigits >= '64' && firstTwoDigits <= '65') ||
      (cleaned.length >= 6 && firstSixDigits >= '622126' && firstSixDigits <= '622925')) {
    return 'Discover';
  }
  
  // JCB: 3528-3589
  if (firstFourDigits >= '3528' && firstFourDigits <= '3589') {
    return 'JCB';
  }
  
  // 中国借记卡 (需要在 UnionPay 信用卡之前检查)
  // 工商银行借记卡: 621226, 621227, 621781, 621785, 621797, 621798, 621799, 622200-622208
  // 建设银行借记卡: 621700, 622280, 622700, 436742, 436745
  // 农业银行借记卡: 622848, 622849, 621282, 621336, 621619, 621622
  // 中国银行借记卡: 621660, 621661, 621662, 621663, 621665, 621667, 621668, 621669
  // 招商银行借记卡: 621485, 621486, 622580, 622588, 622598, 622609
  // 交通银行借记卡: 621436, 621335, 622258, 622259, 622260, 622261
  // 其他常见借记卡BIN: 621xxx, 622xxx, 623xxx, 624xxx, 625xxx, 626xxx, 627xxx, 628xxx
  if (cleaned.length >= 6) {
    const debitBINPrefixes = [
      '621226', '621227', '621781', '621785', '621797', '621798', '621799',
      '621700', '622280', '622700', '436742', '436745',
      '622848', '622849', '621282', '621336', '621619', '621622',
      '621660', '621661', '621662', '621663', '621665', '621667', '621668', '621669',
      '621485', '621486', '622580', '622588', '622598', '622609',
      '621436', '621335', '622258', '622259', '622260', '622261'
    ];
    
    if (debitBINPrefixes.some(prefix => firstSixDigits.startsWith(prefix.substring(0, Math.min(prefix.length, 6))))) {
      return '借记卡 (Debit)';
    }
    
    // 通用借记卡范围检测 (621xxx - 628xxx，排除已知的信用卡范围)
    if (firstThreeDigits >= '621' && firstThreeDigits <= '628' && 
        !(firstSixDigits >= '622126' && firstSixDigits <= '622925')) {
      return '借记卡 (Debit)';
    }
  }
  
  // UnionPay 信用卡: 62xx (not Discover range and not Debit range)
  if (firstTwoDigits === '62' && !(cleaned.length >= 6 && firstSixDigits >= '622126' && firstSixDigits <= '622925')) {
    return 'UnionPay';
  }
  
  // Diners Club: 300-305, 36xx, 38xx
  if ((firstThreeDigits >= '300' && firstThreeDigits <= '305') ||
      firstTwoDigits === '36' || firstTwoDigits === '38') {
    return 'Diners Club';
  }
  
  // Maestro: 5018, 5020, 5038, 5893, 6304, 6759, 6761-6763
  if (firstFourDigits === '5018' || firstFourDigits === '5020' ||
      firstFourDigits === '5038' || firstFourDigits === '5893' ||
      firstFourDigits === '6304' || firstFourDigits === '6759' ||
      (firstFourDigits >= '6761' && firstFourDigits <= '6763')) {
    return 'Maestro';
  }
  
  return '未知类型';
};

// 验证表单字段
export const validateAccountInfo = (accountInfo: AccountInfo): {
  isValid: boolean;
  errors: Record<string, string>;
} => {
  const errors: Record<string, string> = {};
  
  if (!accountInfo.holderName.trim()) {
    errors.holderName = '持卡人姓名不能为空';
  } else if (accountInfo.holderName.trim().length < 2) {
    errors.holderName = '持卡人姓名至少2个字符';
  }
  
  if (!accountInfo.country.trim()) {
    errors.country = '请选择国家/地区';
  }
  
  if (!accountInfo.province.trim()) {
    errors.province = '请选择省/州';
  }
  
  if (!accountInfo.city.trim()) {
    errors.city = '城市不能为空';
  }
  
  if (!accountInfo.district.trim()) {
    errors.district = '区/县不能为空';
  }
  
  if (!accountInfo.postalCode.trim()) {
    errors.postalCode = '邮编不能为空';
  } else if (!validatePostalCode(accountInfo.postalCode, accountInfo.countryCode || 'CN')) {
    errors.postalCode = `请输入正确的${getPostalCodeHint(accountInfo.countryCode || 'CN')}`;
  }
  
  if (!accountInfo.street.trim()) {
    errors.street = '街道地址不能为空';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};
